/**
 * LastSyncedBadge — small, subtle relative-time indicator for cached data.
 * Tells the user "this list was last refreshed N minutes ago" so they know
 * whether to trust the numbers or hit Sync. Used on the TikTok list pages
 * since their data model is sync-from-TikTok-on-demand rather than live.
 */
import React from 'react';
import clsx from 'clsx';
import { Clock } from 'lucide-react';

interface Props {
  /** ISO timestamps from each row's `lastSyncedAt`. We use the most recent. */
  timestamps: Array<string | undefined | null>;
  className?: string;
}

function relativeTime(then: number, now: number): string {
  const diffSec = Math.max(0, Math.round((now - then) / 1000));
  if (diffSec < 30) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const min = Math.round(diffSec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.round(hr / 24);
  return `${day} day${day === 1 ? '' : 's'} ago`;
}

export const LastSyncedBadge: React.FC<Props> = ({ timestamps, className }) => {
  const valid = timestamps
    .filter((t): t is string => !!t)
    .map(t => Date.parse(t))
    .filter(n => Number.isFinite(n));

  if (valid.length === 0) return null;

  const newest = Math.max(...valid);
  const ageMs = Date.now() - newest;
  const stale = ageMs > 10 * 60 * 1000; // > 10 min

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-2xs font-medium',
        stale
          ? 'border-warning/30 bg-warning-soft text-warning'
          : 'border-border bg-surface-alt text-text-muted',
        className,
      )}
      title={new Date(newest).toLocaleString()}
    >
      <Clock size={10} />
      Synced {relativeTime(newest, Date.now())}
    </span>
  );
};
